"use client";

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Target, Heart, Zap, Globe } from 'lucide-react';
export default function AboutUs() {
  const $ = _c(31);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      threshold: 0.2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    ref,
    isVisible
  } = useScrollAnimation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      icon: Target,
      title: "Our Mission",
      description: "Democratize career opportunities by connecting talented learners with companies that value their skills, not just their resumes."
    }, {
      icon: Heart,
      title: "Student First",
      description: "Every decision we make is focused on helping students learn, grow, and land their dream opportunities."
    }, {
      icon: Zap,
      title: "Innovation",
      description: "We constantly evolve our platform using AI and data to create better matches and learning experiences."
    }, {
      icon: Globe,
      title: "Global Impact",
      description: "Breaking down geographical barriers to create opportunities for talented individuals worldwide."
    }];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const values = t1;
  const t2 = ref as React.RefObject<HTMLElement>;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      y: 20
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isVisible) {
    t4 = isVisible ? {
      opacity: 1,
      y: 0
    } : {};
    $[3] = isVisible;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      duration: 0.6
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><Heart className="w-4 h-4" /><span>About Us</span></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Building the Future of<span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Career Development</span></h2>;
    t8 = <p className="text-xl text-gray-600 max-w-3xl mx-auto">We're on a mission to transform how people learn and how companies hire</p>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t4) {
    t9 = <motion.div initial={t3} animate={t4} transition={t5} className="text-center mb-16">{t6}{t7}{t8}</motion.div>;
    $[9] = t4;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: 0,
      y: 20
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== isVisible) {
    t11 = isVisible ? {
      opacity: 1,
      y: 0
    } : {};
    $[12] = isVisible;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      duration: 0.6,
      delay: 0.2
    };
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div><h3 className="text-3xl font-bold text-gray-900 mb-6">Why We Built This</h3><p className="text-gray-600 mb-4 leading-relaxed">The traditional job market is broken. Students spend thousands on education only to struggle finding opportunities. Companies waste months searching for qualified candidates.</p><p className="text-gray-600 mb-4 leading-relaxed">We created Learn2Link to bridge this gap. By combining world-class education, competitive learning, and AI-powered matching, we're creating a new pathway from learning to earning.</p><p className="text-gray-600 leading-relaxed">Every day, we help students land opportunities and companies find talent. That's what drives us.</p></div>;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 text-center"><div className="text-4xl font-bold text-[#6059f7] mb-2">10K+</div><div className="text-gray-600">Active Students</div></div>;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 text-center"><div className="text-4xl font-bold text-[#6059f7] mb-2">500+</div><div className="text-gray-600">Partner Companies</div></div>;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 text-center"><div className="text-4xl font-bold text-[#6059f7] mb-2">95%</div><div className="text-gray-600">Success Rate</div></div>;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl"><div className="grid md:grid-cols-2 gap-12 items-center">{t13}<div className="grid grid-cols-2 gap-6">{t14}{t15}{t16}<div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 text-center"><div className="text-4xl font-bold text-[#6059f7] mb-2">50+</div><div className="text-gray-600">Countries</div></div></div></div></div>;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== t11) {
    t18 = <motion.div initial={t10} animate={t11} transition={t12} className="mb-16">{t17}</motion.div>;
    $[20] = t11;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== isVisible) {
    t19 = <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">{values.map((value, index) => <motion.div key={index} initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4 + index * 0.1
      }}><div className="bg-white rounded-xl p-6 h-full shadow-lg hover:shadow-2xl transition-shadow"><div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mb-4"><value.icon className="w-6 h-6 text-white" /></div><h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3><p className="text-gray-600">{value.description}</p></div></motion.div>)}</div>;
    $[22] = isVisible;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t18 || $[25] !== t19 || $[26] !== t9) {
    t20 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t9}{t18}{t19}</div>;
    $[24] = t18;
    $[25] = t19;
    $[26] = t9;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t2 || $[29] !== t20) {
    t21 = <section id="about" className="py-20 md:py-32 bg-gradient-to-br from-indigo-50 to-purple-50" ref={t2}>{t20}</section>;
    $[28] = t2;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  return t21;
}